import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveCompany } from '@/contexts/ActiveCompanyContext';
import { useI18n } from '@/i18n/useI18n';
import { Icon } from '@/components/ui/Icon';
import { LanguageSwitcher } from './LanguageSwitcher';
import { HOME_ITEM, SETTING_ITEM, buildNavGroups, roleTitleKey, type NavItem } from './navigation';
import { useDocumentTitle } from './useDocumentTitle';
import { useTenantNames } from './useTenantNames';

const SIDEBAR_ID = 'app-sidebar';

/**
 * #2133 設計 §3/§4 + デザイン: 左サイドバー(lg 以上は常時表示の 240px、
 * それより狭い画面はメニューボタンで開くドロワー)と、上部バー
 * (ロールの名前・会社名/スタジオ名・ログイン中のメールアドレス)。
 * メニューに出す画面は navigation.ts の1か所で決める。
 */
export function Layout() {
  const { user, signOut, roleFlags } = useAuth();
  const { activeCompanyId, activeCompanyName, clearActiveCompany } = useActiveCompany();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const roleTitle = t(roleTitleKey(roleFlags));
  useDocumentTitle(roleTitle);

  const isTenantRole = roleFlags.isCompanyAssociate || roleFlags.isStudioAssociate;
  const tenant = useTenantNames(
    isTenantRole ? roleFlags.companyId : null,
    roleFlags.isStudioAssociate ? roleFlags.studioId : null
  );
  const tenantLabel = [tenant.companyName, tenant.studioName].filter((name): name is string => name !== null).join(' / ');

  // #2133(社長指示2026-10-13): admin が会社を選んでいる間は、その会社名を
  // 「運営として管理中」のバナーとして出す(companyAssociate/studioAssociate
  // の会社名/スタジオ名表示とは別枠。切り替えリンクで会社一覧へ戻れる)。
  const isManagingCompany = roleFlags.isAdmin && activeCompanyId !== null;
  const handleSwitchCompany = () => {
    clearActiveCompany();
    navigate('/companies');
  };

  const groups = buildNavGroups(roleFlags, activeCompanyId !== null);

  const closeNavAndRestoreFocus = () => {
    setIsNavOpen(false);
    menuButtonRef.current?.focus();
  };

  useEffect(() => {
    if (!isNavOpen) {
      return;
    }
    closeButtonRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsNavOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isNavOpen]);

  const renderLink = (item: NavItem) => (
    <li key={item.to}>
      <NavLink
        to={item.to}
        end={item.to === '/'}
        onClick={() => setIsNavOpen(false)}
        className={({ isActive }) =>
          clsx(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-bold transition-colors',
            isActive ? 'bg-primary-soft text-primary' : 'text-text-muted hover:bg-canvas hover:text-text'
          )
        }
      >
        <Icon name={item.icon} />
        {t(item.labelKey)}
      </NavLink>
    </li>
  );

  return (
    <div className="min-h-screen bg-canvas text-text">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:shadow-lg"
      >
        {t('layout.skipToContent')}
      </a>

      {isNavOpen && (
        <div className="fixed inset-0 z-30 bg-text/40 lg:hidden" onClick={closeNavAndRestoreFocus} aria-hidden="true" />
      )}

      <aside
        id={SIDEBAR_ID}
        className={clsx(
          'fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-border bg-surface transition-transform duration-200',
          'lg:visible lg:translate-x-0',
          isNavOpen ? 'visible translate-x-0 shadow-xl' : 'invisible -translate-x-full'
        )}
      >
        <div className="flex h-16 shrink-0 items-center gap-3 border-b border-border px-5">
          <img src="/logo.png" alt="" className="h-8 w-8 rounded-lg" />
          <span className="text-lg font-bold tracking-tight text-text">yoctoApp</span>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={closeNavAndRestoreFocus}
            aria-label={t('layout.closeMenu')}
            className="ml-auto rounded-lg p-1.5 text-text-muted hover:bg-canvas lg:hidden"
          >
            <Icon name="close" />
          </button>
        </div>

        <nav aria-label={t('layout.mainNav')} className="flex flex-1 flex-col overflow-y-auto px-3 py-4">
          <ul className="space-y-1">{renderLink(HOME_ITEM)}</ul>

          {groups.map((group) => {
            const headingId = `nav-group-${group.labelKey ?? 'default'}`.replace(/\./g, '-');
            return (
              <div key={group.labelKey ?? 'default'} className="mt-6">
                {group.labelKey && (
                  <p id={headingId} className="mb-2 px-3 text-xs font-bold tracking-wide text-text-muted">
                    {t(group.labelKey)}
                  </p>
                )}
                <ul className="space-y-1" aria-labelledby={group.labelKey ? headingId : undefined}>
                  {group.items.map(renderLink)}
                </ul>
              </div>
            );
          })}

          <ul className="mt-auto space-y-1 border-t border-border pt-4">{renderLink(SETTING_ITEM)}</ul>
        </nav>

        <div className="shrink-0 space-y-2 border-t border-border px-3 py-4">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={() => void signOut()}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-bold text-text-muted hover:bg-canvas hover:text-text"
          >
            <Icon name="logout" />
            {t('common.logout')}
          </button>
        </div>
      </aside>

      <div className="lg:flex">
        {/* 固定表示のサイドバーの背面に、ページの高さいっぱいの白い列を敷く
            (長いページでサイドバーの背景が途中で切れて見えないように) */}
        <div className="hidden w-60 shrink-0 border-r border-border bg-surface lg:block" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-surface/95 px-4 backdrop-blur sm:px-6 lg:px-8">
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setIsNavOpen(true)}
              aria-label={t('layout.openMenu')}
              aria-expanded={isNavOpen}
              aria-controls={SIDEBAR_ID}
              className="-ml-1 rounded-lg p-2 text-text-muted hover:bg-canvas lg:hidden"
            >
              <Icon name="menu" className="h-6 w-6" />
            </button>

            <div className="min-w-0 flex-1">
              <p className="truncate text-base font-bold text-text">{roleTitle}</p>
              {isManagingCompany ? (
                <p className="truncate text-xs text-text-muted">
                  {t('layout.managingCompany', { name: activeCompanyName ?? '' })}{' '}
                  <button
                    type="button"
                    onClick={handleSwitchCompany}
                    className="font-bold text-primary underline-offset-2 hover:underline"
                  >
                    {t('layout.switchCompany')}
                  </button>
                </p>
              ) : tenant.loading ? (
                <span className="mt-1 block h-3 w-40 animate-pulse rounded bg-border" aria-hidden="true" />
              ) : (
                tenantLabel && <p className="truncate text-xs text-text-muted">{tenantLabel}</p>
              )}
            </div>

            {user?.email && (
              <div className="hidden min-w-0 items-center gap-2 sm:flex">
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-soft text-sm font-bold text-primary"
                  aria-hidden="true"
                >
                  {user.email.charAt(0).toUpperCase()}
                </span>
                <span className="max-w-56 truncate text-sm text-text-muted">{user.email}</span>
              </div>
            )}
          </header>

          <main id="main-content" tabIndex={-1} className="mx-auto max-w-6xl px-4 py-8 focus:outline-none sm:px-6 lg:px-8">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
